// src/components/BookCard.jsx - Fixed Navigation Path
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { FaBook, FaUserEdit, FaCalendarAlt, FaClone, FaBookmark, FaHashtag, FaTimes, FaInfoCircle } from 'react-icons/fa';
import axios from 'axios';
import Swal from 'sweetalert2';
import { jwtDecode } from 'jwt-decode';

const BookCard = ({ book, onBorrowSuccess }) => {
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const isAvailable = book.stok_tersedia > 0;
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (isModalOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isModalOpen]);

    const today = new Date();
    const defaultBorrowDate = today.toISOString().slice(0, 10);
    const returnDate = new Date(today);
    returnDate.setDate(today.getDate() + 7);
    const defaultReturnDate = returnDate.toISOString().slice(0, 10);

    const [formData, setFormData] = useState({
        tanggal_peminjaman: defaultBorrowDate,
        tanggal_kembali: defaultReturnDate,
    });

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleBorrowClick = (e) => {
        e.stopPropagation();
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    const handleCardClick = () => {
        // ✅ FIXED: Gunakan path yang konsisten
        navigate(`/user/book/${book.id}`);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                Swal.fire('Gagal', 'Anda harus login untuk meminjam buku.', 'error');
                return;
            }

            const decoded = jwtDecode(token);
            const id_anggota = decoded.id;

            const headers = { 'x-auth-token': token };
            const data = {
                id_anggota,
                id_buku: book.id,
                ...formData
            };

            const response = await axios.post(`${process.env.REACT_APP_API_URL}/peminjaman`, data, { headers });

            Swal.fire({
                title: 'Pengajuan Berhasil!',
                html: `
                    <div style="text-align: left; padding: 10px;">
                        <p style="margin-bottom: 10px;">${response.data.message}</p>
                        <div style="background-color: #fff3cd; padding: 12px; border-radius: 6px; border-left: 4px solid #ffc107;">
                            <p style="margin: 0; font-size: 14px; color: #856404;">
                                <strong>⏱️ Status:</strong> Menunggu Persetujuan Admin<br>
                                <strong>⚠️ Perhatian:</strong> Pengajuan akan hangus dalam 24 jam jika tidak disetujui
                            </p>
                        </div>
                        <p style="margin-top: 12px; font-size: 13px; color: #666;">
                            Cek status pengajuan Anda di menu <strong>"Peminjaman Buku"</strong>
                        </p>
                    </div>
                `,
                icon: 'success',
                confirmButtonText: 'OK',
                confirmButtonColor: '#3273dc'
            });

            closeModal();
            if (onBorrowSuccess) {
                onBorrowSuccess();
            }

        } catch (error) {
            console.error('Error during borrowing:', error.response || error);
            const errorMessage = error.response?.data?.message || 
                               error.response?.data?.detail || 
                               'Gagal mengajukan peminjaman buku. Silakan coba lagi.';
            
            Swal.fire({
                title: 'Gagal',
                text: errorMessage,
                icon: 'error',
                confirmButtonColor: '#3273dc'
            });
        }
    };

    const getUniqueKategori = () => {
        if (!book.nama_kategori) return '-';
        const kategoris = book.nama_kategori
            .split(',')
            .map(k => k.trim())
            .filter(k => k);
        return [...new Set(kategoris)].join(', ');
    };

    const cardStyle = {
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        padding: isMobile ? '12px' : '16px',
        marginBottom: '16px',
        backgroundColor: '#fff',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        position: 'relative',
        transition: 'all 0.3s ease',
        cursor: 'pointer'
    };

    const imageContainerStyle = {
        flexShrink: 0,
        width: isMobile ? '100%' : '120px',
        height: isMobile ? '200px' : '180px',
        backgroundColor: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '4px',
        overflow: 'hidden',
        marginBottom: isMobile ? '12px' : '0'
    };

    const contentStyle = {
        flexGrow: 1,
        marginLeft: isMobile ? '0' : '24px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        minWidth: 0
    };

    const titleStyle = {
        fontSize: isMobile ? '1.1rem' : '1.2rem',
        fontWeight: 'bold',
        color: '#333',
        marginBottom: '8px',
        lineHeight: '1.3',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    };

    const infoRowStyle = {
        display: 'flex',
        alignItems: 'flex-start',
        color: '#666',
        fontSize: isMobile ? '0.85rem' : '0.9rem',
        marginBottom: '4px',
        lineHeight: '1.4'
    };

    const buttonContainerStyle = {
        flexShrink: 0,
        marginLeft: isMobile ? '0' : '24px',
        marginTop: isMobile ? '12px' : '0',
        display: 'flex',
        flexDirection: isMobile ? 'row' : 'column',
        justifyContent: isMobile ? 'space-between' : 'center',
        alignItems: isMobile ? 'center' : 'stretch',
        gap: isMobile ? '8px' : '8px'
    };

    const statusStyle = {
        padding: isMobile ? '4px 8px' : '6px 10px',
        borderRadius: '5px',
        backgroundColor: isAvailable ? '#e6f4ea' : '#fce8e6',
        color: isAvailable ? '#1e4620' : '#c5221f',
        fontWeight: 'bold',
        display: 'inline-block',
        textAlign: 'center',
        fontSize: isMobile ? '0.8rem' : '0.9rem',
        width: isMobile ? 'auto' : '120px',
        whiteSpace: 'nowrap'
    };

    const buttonStyle = {
        padding: isMobile ? '8px 12px' : '10px 20px',
        borderRadius: '5px',
        border: 'none',
        color: 'white',
        fontWeight: 'bold',
        cursor: 'pointer',
        fontSize: isMobile ? '0.85rem' : '1rem',
        whiteSpace: 'nowrap',
        transition: 'background-color 0.2s'
    };

    const InfoRow = ({ icon, children, truncate = false }) => (
        <div style={infoRowStyle}>
            {React.cloneElement(icon, {
                style: {
                    marginRight: '8px',
                    color: '#888',
                    flexShrink: 0,
                    marginTop: '2px'
                }
            })}
            <span style={{
                overflow: truncate ? 'hidden' : 'visible',
                textOverflow: truncate ? 'ellipsis' : 'clip',
                whiteSpace: truncate ? 'nowrap' : 'normal',
                lineHeight: '1.4'
            }}>
                {children}
            </span>
        </div>
    );

    const ModalContent = () => (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
            padding: isMobile ? '20px' : '40px',
            overflowY: 'auto'
        }} onClick={closeModal}>
            <div style={{
                backgroundColor: '#fff',
                borderRadius: '12px',
                width: '100%',
                maxWidth: '500px',
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
                position: 'relative'
            }} onClick={(e) => e.stopPropagation()}>
                <div style={{
                    padding: isMobile ? '15px 20px' : '20px 24px',
                    borderBottom: '1px solid #eee',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <h3 style={{
                        margin: 0,
                        fontSize: isMobile ? '1.2rem' : '1.4rem',
                        fontWeight: 'bold',
                        color: '#333'
                    }}>
                        Ajukan Peminjaman Buku
                    </h3>
                    <button
                        onClick={closeModal}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '1.5rem',
                            cursor: 'pointer',
                            padding: '5px',
                            color: '#666',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '32px',
                            height: '32px',
                            borderRadius: '4px',
                            transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    >
                        <FaTimes />
                    </button>
                </div>

                <div style={{ padding: isMobile ? '20px' : '24px' }}>
                    <div style={{
                        backgroundColor: '#e7f3ff',
                        border: '1px solid #b3d9ff',
                        borderRadius: '6px',
                        padding: '12px',
                        marginBottom: '20px',
                        fontSize: '13px',
                        lineHeight: '1.5'
                    }}>
                        <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: '#0066cc' }}>
                            ℹ️ Informasi Penting:
                        </p>
                        <ul style={{ margin: 0, paddingLeft: '20px', color: '#333' }}>
                            <li>Pengajuan memerlukan <strong>persetujuan admin</strong></li>
                            <li>Maksimal menunggu <strong>24 jam</strong></li>
                            <li>Limit <strong>3 buku per hari</strong></li>
                            <li>Default peminjaman <strong>7 hari</strong></li>
                        </ul>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{
                                display: 'block',
                                marginBottom: '6px',
                                fontWeight: '600',
                                color: '#333',
                                fontSize: '14px'
                            }}>
                                Judul Buku
                            </label>
                            <input
                                type="text"
                                value={book.judul}
                                disabled
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    border: '1px solid #ddd',
                                    borderRadius: '6px',
                                    backgroundColor: '#f5f5f5',
                                    boxSizing: 'border-box',
                                    fontSize: '14px',
                                    color: '#666'
                                }}
                            />
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={{
                                display: 'block',
                                marginBottom: '6px',
                                fontWeight: '600',
                                color: '#333',
                                fontSize: '14px'
                            }}>
                                Tanggal Peminjaman
                            </label>
                            <input
                                type="date"
                                name="tanggal_peminjaman"
                                value={formData.tanggal_peminjaman}
                                onChange={handleChange}
                                required
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    border: '1px solid #ddd',
                                    borderRadius: '6px',
                                    boxSizing: 'border-box',
                                    fontSize: '14px'
                                }}
                            />
                        </div>

                        <div style={{ marginBottom: '24px' }}>
                            <label style={{
                                display: 'block',
                                marginBottom: '6px',
                                fontWeight: '600',
                                color: '#333',
                                fontSize: '14px'
                            }}>
                                Tanggal Kembali
                            </label>
                            <input
                                type="date"
                                name="tanggal_kembali"
                                value={formData.tanggal_kembali}
                                onChange={handleChange}
                                required
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    border: '1px solid #ddd',
                                    borderRadius: '6px',
                                    boxSizing: 'border-box',
                                    fontSize: '14px'
                                }}
                            />
                        </div>

                        <button
                            type="submit"
                            style={{
                                width: '100%',
                                padding: '12px',
                                backgroundColor: '#3273dc',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                fontSize: '16px',
                                transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#2366d1'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#3273dc'}
                        >
                            Ajukan Peminjaman
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );

    return (
        <>
            <div 
                style={cardStyle}
                onClick={handleCardClick}
                onMouseEnter={(e) => {
                    if (!isMobile) {
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                    }
                }}
                onMouseLeave={(e) => {
                    if (!isMobile) {
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                        e.currentTarget.style.transform = 'translateY(0)';
                    }
                }}
            >
                <div style={imageContainerStyle}>
                    {book.foto_buku ? (
                        <img
                            src={`${process.env.REACT_APP_BASE_URL}/images/buku/${book.foto_buku}`}
                            alt={book.judul}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                            }}
                        />
                    ) : (
                        <div style={{ color: '#ccc', fontSize: isMobile ? '60px' : '100px' }}>
                            <FaBook />
                        </div>
                    )}
                </div>

                <div style={contentStyle}>
                    <h3 style={titleStyle}>{book.judul}</h3>

                    <InfoRow icon={<FaUserEdit />} truncate={!isMobile}>
                        Pengarang: {book.pengarang || '-'}
                    </InfoRow>

                    <InfoRow icon={<FaCalendarAlt />}>
                        Tahun Terbit: {book.tahun_terbit || '-'}
                    </InfoRow>

                    <InfoRow icon={<FaClone />}>
                        Edisi: {book.edisi || '-'}
                    </InfoRow>

                    <InfoRow icon={<FaBookmark />} truncate={!isMobile}>
                        Penerbit: {book.penerbit || '-'}
                    </InfoRow>

                    <InfoRow icon={<FaHashtag />} truncate={!isMobile}>
                        Kategori: {getUniqueKategori()}
                    </InfoRow>

                    {!isMobile && isAvailable && (
                        <p style={{
                            marginTop: '4px',
                            fontSize: '0.85rem',
                            color: '#888'
                        }}>
                            Stok: {book.stok_tersedia}
                        </p>
                    )}

                    {isMobile && (
                        <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={statusStyle}>
                                {isAvailable ? 'Tersedia' : 'Tidak Tersedia'}
                            </div>
                            {isAvailable && (
                                <span style={{
                                    fontSize: '0.8rem',
                                    color: '#888'
                                }}>
                                    Stok: {book.stok_tersedia}
                                </span>
                            )}
                        </div>
                    )}
                </div>

                <div style={buttonContainerStyle}>
                    {!isMobile && (
                        <div>
                            <div style={statusStyle}>
                                {isAvailable ? 'Tersedia' : 'Tidak Tersedia'}
                            </div>
                        </div>
                    )}

                    <button
                        style={{
                            ...buttonStyle,
                            backgroundColor: '#17a2b8',
                            flex: isMobile ? '1' : 'unset'
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/user/book/${book.id}`);
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#138496'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#17a2b8'}
                    >
                        <FaInfoCircle style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                        Detail
                    </button>

                    <button
                        style={{
                            ...buttonStyle,
                            backgroundColor: isAvailable ? '#3273dc' : '#888',
                            cursor: isAvailable ? 'pointer' : 'not-allowed',
                            flex: isMobile ? '1' : 'unset'
                        }}
                        onClick={handleBorrowClick}
                        disabled={!isAvailable}
                        onMouseEnter={(e) => {
                            if (isAvailable) e.target.style.backgroundColor = '#2366d1';
                        }}
                        onMouseLeave={(e) => {
                            if (isAvailable) e.target.style.backgroundColor = '#3273dc';
                        }}
                    >
                        Pinjam
                    </button>
                </div>
            </div>

            {isModalOpen && ReactDOM.createPortal(
                <ModalContent />,
                document.body
            )}
        </>
    );
};

export default BookCard;